'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';

import { Card } from '@eliadmin/core/src/components/ui/card';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { getTextFromHTML } from '@eliadmin/core/src/utils/misc-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import type { ComponentProps } from 'react';
import { ProductBadge } from '@eliadmin/core/src/components/product/detail/product-badge';
import { Heading } from '@eliadmin/core/src/components/ui/heading';
import { Paragraph } from '@eliadmin/core/src/components/ui/paragraph';
import { calculateReadingTime } from '@eliadmin/core/src/utils/calculate-reading-time';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export type BlogCardProps = Omit<ComponentProps<typeof Card>, 'className'> & {
  blog: Blog;
  headingTag?: 'h2' | 'h3';
  imagePriority?: boolean;
};

export function BlogCard({ blog, headingTag = 'h3', imagePriority = false }: BlogCardProps) {
  const { t } = useTranslations();

  const textContent = getTextFromHTML(blog.content);
  const excerptHtml = (blog.content ?? '').replace(/<\/?a\b[^>]*>/gi, '');
  const locale = useLocaleStore(state => state.currentLocale);

  let formattedDate;

  try {
    formattedDate = new Date(blog.created_at).toLocaleDateString(locale);
  } catch (error) {
    if (error instanceof RangeError) {
      formattedDate = new Date(blog.created_at).toLocaleDateString('en-US');
    } else {
      throw error;
    }
  }

  return (
    <Link
      aria-label={getTextFromHTML(blog.name).trim() || undefined}
      href={ROUTES.BLOG_DETAIL({ slug: blog.slug })}
      className="col-span-1 flex w-full flex-col items-start justify-start rounded-[12px] border bg-white"
    >
      <div className="relative h-[183px] w-full overflow-hidden rounded-t-[12px] p-3 sm:h-[220px] sm:p-4 lg:p-6">
        <div className="relative flex w-full flex-wrap items-start justify-start gap-2">
          {blog.categories?.map(category => (
            <ProductBadge
              background="#FFFFFF"
              key={category.name}
              color="#21262A"
              text={category.name}
            />
          ))}
        </div>
        <Image
          src={blog?.image?.url ?? '#'}
          alt={resolveImageAlt(blog?.image, blog?.name)}
          fill
          sizes="(max-width: 639px) calc(100vw - 32px), (max-width: 1023px) 50vw, 384px"
          loading={imagePriority ? 'eager' : 'lazy'}
          priority={imagePriority}
          fetchPriority={imagePriority ? 'high' : undefined}
          className="absolute inset-0 z-0 h-full w-full object-cover object-center"
        />
      </div>
      <div className="flex w-full flex-col items-start justify-start gap-3 p-3 sm:p-4 lg:p-6">
        <div className="flex w-full flex-col items-start justify-start gap-[6px] sm:gap-2">
          <Heading
            title={blog.name}
            tag={headingTag}
            visualTag="h5"
            className={{ title: 'line-clamp-2 h-12' }}
          />
          <Paragraph
            innerHtml={excerptHtml}
            className="line-clamp-3 h-[62px] w-full"
            color="muted-foreground"
            size="sm"
          />
        </div>
        <Paragraph className="w-full" size="xs" color="muted-foreground">
          {formattedDate} · {calculateReadingTime(textContent)} {t('text3360')}
        </Paragraph>
      </div>
    </Link>
  );
}
